/**
 * InvoicesList Component
 * Display list of invoices with pagination
 *
 * @author SAUBHAGYA Development Team
 * @since US-INV-001
 */

import React from 'react';
import { AlertCircle, Package, ChevronLeft, ChevronRight, Eye, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { InvoiceCard } from './InvoiceCard';
import { formatCurrency, formatDate } from '../utils/invoice.utils';
import type { InvoicesResponse, InvoiceSummary } from '../types/invoice.types';

// ============================================
// SKELETON LOADER
// ============================================

const InvoiceSkeleton = () => (
  <Card className="p-4 mb-4 border-slate-200 animate-pulse">
    <div className="space-y-3">
      <div className="h-6 bg-slate-200 rounded w-1/3" />
      <div className="h-4 bg-slate-200 rounded w-1/2" />
      <div className="flex justify-between">
        <div className="h-4 bg-slate-200 rounded w-1/4" />
        <div className="h-4 bg-slate-200 rounded w-1/4" />
      </div>
    </div>
  </Card>
);

// ============================================
// STATUS BADGE CONFIGURATION
// ============================================

const STATUS_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  DRAFT: { bg: 'bg-slate-100', text: 'text-slate-800', label: 'Draft' },
  ISSUED: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Issued' },
  PAID: { bg: 'bg-green-100', text: 'text-green-800', label: 'Paid' },
  PARTIALLY_PAID: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Partially Paid' },
  OVERDUE: { bg: 'bg-red-100', text: 'text-red-800', label: 'Overdue' },
  CANCELLED: { bg: 'bg-slate-100', text: 'text-slate-800', label: 'Cancelled' },
};

function getStatusBadge(status: string): React.ReactNode {
  const style = STATUS_STYLES[status] || {
    bg: 'bg-gray-100',
    text: 'text-gray-800',
    label: status,
  };
  return (
    <Badge className={`${style.bg} ${style.text} border-0`}>
      {style.label}
    </Badge>
  );
}

// ============================================
// COMPONENT PROPS
// ============================================

export interface InvoicesListProps {
  data?: InvoicesResponse;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  onViewDetails: (invoiceId: number) => void;
  onDeliveryManagement?: (invoiceId: number) => void;
  onPageChange: (page: number) => void;
  currentPage: number;
  viewMode?: 'list' | 'table';
}

// ============================================
// COMPONENT
// ============================================

/**
 * InvoicesList Component
 * Renders list of invoices with loading, error, and empty states
 *
 * @example
 * ```tsx
 * <InvoicesList
 *   data={invoices}
 *   isLoading={loading}
 *   isError={error}
 *   error={errorObject}
 *   onViewDetails={handleViewDetails}
 *   onPageChange={handlePageChange}
 *   currentPage={0}
 * />
 * ```
 */
export function InvoicesList({
  data,
  isLoading,
  isError,
  error,
  onViewDetails,
  onDeliveryManagement,
  onPageChange,
  currentPage,
  viewMode = 'list',
}: InvoicesListProps) {
  // Loading State
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(4)].map((_, i) => (
          <InvoiceSkeleton key={i} />
        ))}
      </div>
    );
  }

  // Error State
  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          {error?.message || 'Failed to load invoices. Please try again.'}
        </AlertDescription>
      </Alert>
    );
  }

  // Empty State
  if (!data?.invoices || data.invoices.length === 0) {
    return (
      <Card className="p-12 text-center border-slate-200 bg-white">
        <Package className="h-12 w-12 text-slate-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          No Invoices Found
        </h3>
        <p className="text-slate-600">
          Try adjusting your filters or search criteria
        </p>
      </Card>
    );
  }

  const totalPages = data?.totalPages || 1;
  const hasNext = data?.hasNext || false;
  const hasPrevious = data?.hasPrevious || false;

  // Table View Rendering
  const renderTableView = () => (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Invoice #</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Customer</TableHead>
            <TableHead>Invoice Date</TableHead>
            <TableHead>Due Date</TableHead>
            <TableHead>Amount</TableHead>
            <TableHead>Balance Due</TableHead>
            <TableHead className="text-center">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data?.invoices.map((invoice) => (
            <TableRow key={invoice.invoiceId} className="hover:bg-gray-50">
              <TableCell className="font-medium">
                {invoice.invoiceNumber}
              </TableCell>
              <TableCell>{getStatusBadge(invoice.status)}</TableCell>
              <TableCell>{invoice.customerName}</TableCell>
              <TableCell className="text-gray-700">
                {formatDate(invoice.invoiceDate)}
              </TableCell>
              <TableCell className="text-gray-700">
                {invoice.dueDate ? formatDate(invoice.dueDate) : 'N/A'}
              </TableCell>
              <TableCell className="font-semibold">
                {formatCurrency(invoice.totalAmount)}
              </TableCell>
              <TableCell>
                {invoice.balanceDue > 0 ? (
                  <span className="text-red-600 font-semibold">
                    {formatCurrency(invoice.balanceDue)}
                  </span>
                ) : (
                  <span className="text-green-600">Paid</span>
                )}
              </TableCell>
              <TableCell className="text-center">
                <div className="flex justify-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onViewDetails(invoice.invoiceId)}
                    aria-label={`View invoice ${invoice.invoiceNumber}`}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  {onDeliveryManagement && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDeliveryManagement(invoice.invoiceId)}
                      aria-label={`Manage delivery for invoice ${invoice.invoiceNumber}`}
                    >
                      <Truck className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  // List View Rendering (Card Layout)
  const renderListView = () => (
    <div className="space-y-4">
      {data?.invoices.map((invoice) => (
        <InvoiceCard
          key={invoice.invoiceId}
          invoice={invoice}
          onViewDetails={onViewDetails}
          onDeliveryManagement={onDeliveryManagement}
        />
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Invoice Cards or Table based on viewMode */}
      {viewMode === 'table' ? renderTableView() : renderListView()}

      {/* Results Summary */}
      <div className="text-sm text-slate-600">
        Showing {data?.invoices.length || 0} of {data?.totalElements || 0} invoices
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={!hasPrevious}
            className="gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>

          <div className="px-3 py-2 text-sm text-slate-600 font-medium">
            Page {currentPage + 1} of {totalPages}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={!hasNext}
            className="gap-1"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

export default InvoicesList;
